"use client";
import { useSpring, animated, useInView, useSprings } from '@react-spring/web';
import { BookOpen, FileCheck, Calendar, Users, Award, Globe } from 'lucide-react';
import { useSeatsData } from '@/hooks/useSeatsData';
import { useEffect } from 'react';
import { trackViewContent } from '../PixelTracker';

export default function InclusionsSection() {
  const { remainingSeats, totalSeats } = useSeatsData();
  const inclusions = [
    {
      icon: BookOpen,
      title: "Herramientas y plantillas oficiales",
      description: "Incluye la herramienta de El Valor de los  Valores y las plantillas profesionales."
    },
    {
      icon: FileCheck,
      title: "Manual completo de coaching",
      description: "Todo lo que necesitas para aplicar la metodología desde el día 1."
    },
    {
      icon: Calendar,
      title: "12 meses de acompañamiento",
      description: "Sesiones quincenales en vivo, mentorías y comunidad 24/7."
    },
    {
      icon: Users,
      title: "Acompañamiento directo de Toño y el equipo",
      description: "Te apoyamos durante todo el proceso: no caminas sola/o."
    },
    {
      icon: Award,
      title: "Diploma oficial",
      description: "Acreditación como profesional certificado/a en CoachingxValores, avalado por Zinquo, ICF y MDS Group."
    },
    {
      icon: Globe,
      title: "Acceso a la comunidad internacional Zinquo",
      description: "Más de 2.000 coaches y facilitadores en todo el mundo. Conexiones, sinergias, red."
    }
  ];

  const organizations = [
    { src: "/Logo ICF.jpg", alt: "Logo ICF International Coach Federation - Acreditación internacional coaching profesional", name: "ICF" },
    { src: "/logotipo-zinquo.png", alt: "Logo Zinquo Centro Internacional en Desarrollo de Valores - Metodología exclusiva coaching valores", name: "Zinquo" },
    { src: "/gestion-mds1-logo.png", alt: "Logo MDS Group Management Development Systems - Formación ejecutiva y desarrollo organizacional", name: "MDS Group" }
  ];

  // Move all hooks to top level
  const [headerRef, headerInView] = useInView({ once: true, rootMargin: '-80px' });
  const [inclusionsRef, inclusionsInView] = useInView({ once: true, rootMargin: '-80px' });
  const [offerRef, offerInView] = useInView({ once: true, rootMargin: '-80px' });
  const [sponsorRef, sponsorInView] = useInView({ once: true, rootMargin: '-80px' });
  const [orgsRef, orgsInView] = useInView({ once: true, rootMargin: '-80px' });
  const [ctaRef, ctaInView] = useInView({ once: true, rootMargin: '-80px' });

  const headerSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: headerInView ? 1 : 0 },
    config: { duration: 400 }
  });

  const offerSpring = useSpring({
    opacity: offerInView ? 1 : 0,
    config: { duration: 400 }
  });

  const sponsorSpring = useSpring({
    opacity: sponsorInView ? 1 : 0,
    config: { duration: 400 }
  });

  const ctaSpring = useSpring({
    opacity: ctaInView ? 1 : 0,
    config: { duration: 400 }
  });

  // Inclusions grid animations
  const inclusionsSprings = useSprings(
    inclusions.length,
    inclusions.map((_, index) => ({
      opacity: inclusionsInView ? 1 : 0,
      transform: inclusionsInView ? 'translateY(0px)' : 'translateY(20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Organizations grid animations
  const orgsSprings = useSprings(
    organizations.length,
    organizations.map((_, index) => ({
      opacity: orgsInView ? 1 : 0,
      transform: orgsInView ? 'translateY(0px)' : 'translateY(20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Track when inclusions section comes into view
  useEffect(() => {
    if (inclusionsInView) {
      trackViewContent('Sección Qué Incluye');
    }
  }, [inclusionsInView]);

  return (
    <section id="inclusions" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* SEO-optimized heading */}
        <animated.div
          ref={headerRef}
          className="text-center mb-16"
          style={headerSpring}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-heading text-foreground mb-4">
            Todo lo que incluye tu certificación
          </h2>
          <p className="text-xl md:text-2xl text-foreground/80 max-w-3xl mx-auto">
            Un programa completo diseñado para tu éxito profesional
          </p>
        </animated.div>

        {/* Inclusions grid */}
        <div ref={inclusionsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {inclusionsSprings.map((style, index) => {
            const item = inclusions[index];
            return (
              <animated.div
                key={index}
                className="bg-background p-6 rounded-xl shadow-lg border border-gray-100 hover:border-accent/30 transition-shadow duration-300"
                style={style}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-[#97d700]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#97d700]" />
                  </div>
                  <div>
                    <h3 className="font-heading uppercase tracking-wider text-lg text-foreground mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </animated.div>
            );
          })}
        </div>

        {/* Limited offer section */}
        <animated.div
          ref={offerRef}
          className="bg-gray-50 p-8 md:p-12 rounded-2xl shadow-xl text-center mb-16"
          style={offerSpring}
        >
          <h3 className="text-2xl md:text-3xl font-heading uppercase tracking-tight text-black mb-4">
            <span className="text-[#97d700]">{remainingSeats}</span> plazas restantes de <span className="text-[#97d700]">{totalSeats}</span> disponibles
          </h3>
          <p className="text-xl md:text-2xl text-black font-medium mb-2">
            Tres días de formación presencial intensiva (formato retiro)
          </p>
          <p className="text-lg text-black/80 max-w-3xl mx-auto">
            Una experiencia transformadora, vivencial y aplicable, en un espacio creado para que te enfoques y disfrutes.
          </p>
        </animated.div>

        {/* Sponsor logos section */}
        <animated.div
          ref={sponsorRef}
          className="mt-20 pt-12 border-t border-gray-200"
          style={sponsorSpring}
        >
          <p className="text-center text-sm text-gray-600 mb-8 uppercase tracking-wider">
            Formación avalada por:
          </p>
          <div ref={orgsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {orgsSprings.map((style, index) => {
              const org = organizations[index];
              return (
                <animated.div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:border-[#97d700]/30 transition-shadow duration-300"
                  style={style}
                >
                  <div className="h-20 flex items-center justify-center mb-4">
                    <img
                      src={org.src}
                      alt={org.alt}
                      width={120}
                      height={80}
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <h4 className="text-lg font-heading uppercase text-center text-black mb-2">
                    {org.name}
                  </h4>
                  <p className="text-sm text-gray-600 text-center">
                    {org.name === 'ICF' ? 'Federación Internacional de Coaching' :
                     org.name === 'Zinquo' ? 'Centro Internacional en Desarrollo de Valores' :
                     'Management Development Systems'}
                  </p>
                </animated.div>
              );
            })}
          </div>
        </animated.div>

        {/* Final CTA */}
        <animated.div
          ref={ctaRef}
          className="text-center mt-16"
          style={ctaSpring}
        >
          <a
            href="#registration"
            className="inline-block px-10 py-5 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold text-xl rounded-full shadow-2xl hover:shadow-[#97d700]/30 transform hover:scale-105 transition-all duration-300"
          >
            ASEGURAR MI LUGAR
          </a>
        </animated.div>
      </div>
    </section>
  );
}
